import React, { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import moment from "moment";
import CustomTable from "../../../components/Tables/CustomTable";
import DropdownMenu from "../../ui/DropdownMenu";
import ClickableUserName from "../../../components/shared/ClickableUserName";
import { useAppSelector } from "../../../redux/app/hooks";
import { PublicContract } from "../../../redux/features/types/publicContractTypes";
import {
  formatNaira,
  getBuyerDisplayName,
  getBuyerUser,
  getContractCode,
  getContractTotal,
  getPaymentStatusClasses,
  getPersonDisplayName,
  getPublicContractStatusClasses,
  getVendorUser,
  labelPaymentStatus,
  labelPublicContractStatus,
} from "./util";

interface PublicContractsTableProps {
  page: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onUpdateStatus: (contract: PublicContract) => void;
}

const StatusPill = ({
  label,
  className,
}: {
  label: string;
  className: string;
}) => (
  <span
    className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${className}`}
  >
    {label}
  </span>
);

const PublicContractsTable: FC<PublicContractsTableProps> = ({
  page,
  onPageChange,
  onLimitChange,
  onUpdateStatus,
}) => {
  const navigate = useNavigate();
  const { list, listLoading } = useAppSelector(
    (state) => state.publicContracts
  );

  const tableData = useMemo(
    () => ({
      columns: [
        { label: "Code", field: "code" },
        { label: "Title", field: "title" },
        { label: "Vendor", field: "vendor" },
        { label: "Buyer", field: "buyer" },
        { label: "Amount", field: "amount" },
        { label: "Status", field: "status" },
        { label: "Payment", field: "paymentStatus" },
        { label: "Created", field: "createdAt" },
        { label: "Action", field: "action" },
      ],
      rows: (list.items ?? []).map((item: PublicContract, index: number) => {
        const vendor = getVendorUser(item);
        const buyer = getBuyerUser(item);
        const buyerName = getBuyerDisplayName(item);
        const vendorName = getPersonDisplayName(vendor);
        const buyerEmail = buyer?.email ? String(buyer.email).trim() : "";
        const vendorEmail = vendor?.email ? String(vendor.email).trim() : "";

        return {
          id: index + 1,
          contractId: item.id,
          code: getContractCode(item),
          title: (
            <span className="max-w-[220px] truncate block" title={item.title}>
              {item.title || "—"}
            </span>
          ),
          vendor: (
            <div className="flex flex-col min-w-0">
              {vendor?.id ? (
                <ClickableUserName
                  user={vendor}
                  userType="VENDOR"
                  displayName={vendorName || undefined}
                />
              ) : vendorName ? (
                <span>{vendorName}</span>
              ) : (
                <span>—</span>
              )}
              {vendorEmail ? (
                <span
                  className="text-xs text-gray-500 truncate"
                  title={vendorEmail}
                >
                  {vendorEmail}
                </span>
              ) : null}
            </div>
          ),
          buyer: (
            <div className="flex flex-col min-w-0">
              {buyer?.id ? (
                <ClickableUserName
                  user={buyer}
                  userType="BUYER"
                  displayName={buyerName !== "—" ? buyerName : undefined}
                />
              ) : (
                <span>{buyerName}</span>
              )}
              {buyerEmail ? (
                <span
                  className="text-xs text-gray-500 truncate"
                  title={buyerEmail}
                >
                  {buyerEmail}
                </span>
              ) : null}
            </div>
          ),
          amount: formatNaira(getContractTotal(item) ?? item.itemsAmount),
          status: (
            <StatusPill
              label={labelPublicContractStatus(item.status)}
              className={getPublicContractStatusClasses(item.status)}
            />
          ),
          paymentStatus: (
            <StatusPill
              label={labelPaymentStatus(item.paymentStatus)}
              className={getPaymentStatusClasses(item.paymentStatus)}
            />
          ),
          createdAt: item.createdAt
            ? moment(item.createdAt).format("Do MMM YYYY, h:mm A")
            : "—",
          action: (
            <DropdownMenu
              options={[
                {
                  label: "View details",
                  onClick: (c: PublicContract) =>
                    navigate(`/invoice-management/${c.id}`),
                  returnAll: true,
                },
                {
                  label: "Update status",
                  onClick: (c: PublicContract) => onUpdateStatus(c),
                  returnAll: true,
                },
              ]}
              icon={<MoreVertical style={{ cursor: "pointer" }} />}
              item={item}
            />
          ),
        };
      }),
    }),
    [list.items, navigate, onUpdateStatus]
  );

  return (
    <CustomTable
      data={tableData}
      loading={listLoading}
      noRecordText="No public contracts found"
      showFooter
      totalPages={list.meta.totalPages}
      itemsPerPage={list.meta.itemsPerPage}
      currentPage={page}
      onPageChange={onPageChange}
      onLimitChange={onLimitChange}
      onRowClick={(row) => {
        if (row?.contractId) {
          navigate(`/invoice-management/${row.contractId}`);
        }
      }}
    />
  );
};

export default PublicContractsTable;
